import React, { useMemo } from 'react'

import PlaceholderImage from './Common/PlaceholderImage'
import SummaryImage from './Common/SummaryImage'
import ReadOnlyItemFooter from './Common/SummaryModalFooter/ReadOnlyItemFooter'
import TourAndCruiseSummaryModalFooter from './Common/SummaryModalFooter/TourAndCruiseSummaryModalFooter'
import RestoreCartModalFooter from '../Common/RestoreCartModalFooter'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import CustomItemSummaryContent from 'tripPlanner/components/TripModal/TripItems/Summary/ToursSummary/CustomItemSummaryContent'
import TourAndCruiseSummaryContent from 'tripPlanner/components/TripModal/TripItems/Summary/ToursSummary/TourAndCruiseSummaryContent'
import TourV2SummaryContent from 'tripPlanner/components/TripModal/TripItems/Summary/ToursSummary/TourV2SummaryContent'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import useOffer from 'hooks/Offers/useOffer'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { CruiseItem, TourItem } from 'tripPlanner/types/tripItem'
import {
  getOfferId,
  isCustomItem,
  isLeBookmark,
  isTour,
  isTourOfferNoSummary,
} from 'tripPlanner/utils'
import { isBookableItem } from 'tripPlanner/utils/bookmark/restoreCart'

interface Props {
  item: CruiseItem | TourItem
}

function TourAndCruiseItemSummary(props: Props) {
  const { item } = props

  const { closeModal, goBack } = useModalCallbacks()

  const canEdit = useCanEditTrip()
  const isBookable = isLeBookmark(item) && isBookableItem(item)

  const offerId = getOfferId(item)
  const [offer, isLoading] = useOffer<App.Offer | App.OfferSummary | App.Tours.TourV2Offer>(offerId)

  const modalContent = useMemo(() => {
    if (isCustomItem(item)) {
      const imageComponent = (
        <SummaryImage
          place={item.startPlace}
          placeholder={<PlaceholderImage itemType={item.type} />}
          tripId={item.tripId}
          tripItemId={item.id}
        />
      )

      return (
        <>
          {imageComponent}
          <CustomItemSummaryContent item={item} />
        </>
      )
    }
    if (isLoading) {
      return <LoadingIndicator inline />
    }
    if (offer && isTourOfferNoSummary(offer)) {
      return <TourAndCruiseSummaryContent item={item} offer={offer} />
    }
    if (offer && isTour(item) && isTourV2Offer(offer)) {
      return <TourV2SummaryContent item={item} offer={offer} />
    }
  }, [item, isLoading, offer])

  return (
    <>
      <ModalHeader
        title={item.name}
        subtitle={item.startPlace.name}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />

      <ModalBody>{modalContent}</ModalBody>

      {canEdit && (
        <>
          {isBookable && <RestoreCartModalFooter item={item} />}
          {!isBookable && <TourAndCruiseSummaryModalFooter item={item} />}
        </>
      )}
      {!canEdit && <ReadOnlyItemFooter item={item} />}
    </>
  )
}

export default TourAndCruiseItemSummary
